import { useEffect, useRef, useState } from "react";
import Lenis from "lenis";
import { translations } from "./i18n";
import Nav from "./components/Nav";
import Hero from "./components/Hero";
import Marquee from "./components/Marquee";
import Manifesto from "./components/Manifesto";
import Waitlist from "./components/Waitlist";
import Funding from "./components/Funding";
import Footer from "./components/Footer";
import AdminArea from "./components/AdminArea";

export default function App() {
  const [locale, setLocale] = useState("it");
  const lenisRef = useRef(null);
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    const API = process.env.REACT_APP_BACKEND_URL;
    const hash = window.location.hash;
    if (hash.includes("session_id=")) {
      const sessionId = hash.split("session_id=")[1].split("&")[0];
      fetch(`${API}/api/auth/session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ session_id: sessionId }),
      })
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => setUser(data))
        .catch(() => setUser(null))
        .finally(() => {
          window.history.replaceState(null, "", window.location.pathname);
          setAuthChecked(true);
        });
      return;
    }
    fetch(`${API}/api/auth/me`, { credentials: "include" })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => setUser(data))
      .catch(() => setUser(null))
      .finally(() => setAuthChecked(true));
  }, []);

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleLogout = async () => {
    await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/logout`, {
      method: "POST",
      credentials: "include",
    });
    setUser(null);
  };

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const lenis = new Lenis({ lerp: 0.09 });
    lenisRef.current = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, []);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const scrollTo = (selector) => {
    if (lenisRef.current) {
      lenisRef.current.scrollTo(selector, { offset: -70, duration: 1.4 });
    } else {
      document.querySelector(selector)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const t = translations[locale];

  if (window.location.pathname === "/admin") {
    return (
      <AdminArea
        locale={locale}
        setLocale={setLocale}
        user={user}
        authChecked={authChecked}
        onLogin={handleLogin}
        onLogout={handleLogout}
      />
    );
  }

  return (
    <div className="min-h-screen bg-void font-body text-bone antialiased">
      <div className="grain-overlay" aria-hidden />
      <Nav
        t={t.nav}
        locale={locale}
        setLocale={setLocale}
        onCta={() => scrollTo("#waitlist")}
        user={user}
        onLogin={handleLogin}
        onLogout={handleLogout}
      />
      <main>
        <Hero
          t={t.hero}
          onPrimary={() => scrollTo("#waitlist")}
          onSecondary={() => scrollTo("#funding")}
        />
        <Marquee items={t.marquee} />
        <Manifesto t={t.manifesto} />
        <Waitlist t={t.waitlist} locale={locale} user={user} />
        <Funding t={t.funding} locale={locale} />
      </main>
      <Footer t={t.footer} locale={locale} onWaitlist={() => scrollTo("#waitlist")} />
    </div>
  );
}
